// src/notifications/components/notification-bell.tsx
// The nav bell + unread badge (PROD-1187) that composes into PractitionerNav's
// footerSlot. Every rendered part is a Signal DS primitive: the tile is `RailAction`
// (icon="bell", `hideCaption` — the footer cluster is glyph-only, see app-shell.tsx), and
// the unread *count overlay* is `NotificationIndicator` (DS 1.45.0) — it
// owns count capping (99+), semantic tone, the role=status/aria-live badge, and the "no
// empty badge at count 0" rule — and the inbox now opens in the DS `AnchoredPopover`, the
// same surface as the account menu, so both rails popovers share one placement, one chrome
// and one open/close motion.
//
// That also deleted this file's hand-rolled outside-click and Escape handlers: the DS
// surface owns dismissal and focus return. The tile is rendered THROUGH
// `AnchoredPopoverTrigger`'s `render` prop so @base-ui owns the open and close transitions
// — see nav-footer-slot.tsx for the measured reason a controlled `onClick` toggle does not
// work here. `render` replaces the trigger element rather than nesting, so the
// NotificationIndicator still wraps exactly one button.
//
// This file only wires data: the server's unseen count
// (GetUserUnSeenNotificationCount) and the open/close state of the inbox panel.
// Opening the panel resets the count (the panel fires ResetNotificationSeenCount on
// mount), matching the Flutter behaviour where the badge clears on open.
import { useRef, useState } from 'react';
import {
  AnchoredPopover,
  AnchoredPopoverContent,
  AnchoredPopoverTrigger,
  NotificationIndicator,
  RailAction,
} from '@scripsteam/scrips-signal-ds';
import {
  RAIL_POPOVER_ALIGN,
  RAIL_POPOVER_SIDE,
  RAIL_POPOVER_SIDE_OFFSET,
} from '@/app/rail-popover-anchoring';
import { useUnseenNotificationCount } from '@/notifications/services/use-unseen-notification-count';
import { NotificationPanel } from './notification-panel';

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const anchorHostRef = useRef<HTMLDivElement>(null);
  const { data: unseenCount = 0 } = useUnseenNotificationCount();

  const showBadge = unseenCount > 0;

  return (
    // `position: relative` is the positioned ancestor the surface resolves against — see
    // nav-footer-slot.tsx for why the portal must land next to the anchor and not on <body>.
    <div ref={anchorHostRef} className="relative">
      <AnchoredPopover open={open} onOpenChange={setOpen}>
        <NotificationIndicator
          count={unseenCount}
          tone="info"
          ariaLabel={showBadge ? `${unseenCount} unread notifications` : undefined}
        >
          <AnchoredPopoverTrigger
            render={<RailAction icon="bell" label="Notifications" hideCaption />}
          />
        </NotificationIndicator>

        <AnchoredPopoverContent
          container={anchorHostRef}
          side={RAIL_POPOVER_SIDE}
          align={RAIL_POPOVER_ALIGN}
          sideOffset={RAIL_POPOVER_SIDE_OFFSET}
          aria-label="Notifications"
          // The inbox is a list, not a menu of actions: it needs a width and a scroll cap
          // that the generic surface has no business defaulting. Showcase look, unconditional (design
          // NotificationPanel `.root`): 360 wide, 560 tall at most, hairline, card radius, white, the
          // overlay elevation (`.notification-popover`, showcase-auth.css).
          className="notification-popover w-90 max-w-popover-viewport max-h-overlay-body overflow-hidden shadow-overlay"
        >
          {open && <NotificationPanel onClose={() => setOpen(false)} />}
        </AnchoredPopoverContent>
      </AnchoredPopover>
    </div>
  );
}
